import React, { createContext, useContext, useState, useCallback } from "react";

const translations = {
  de: {
    nav: {
      services: "Leistungen",
      spectrum: "Spektrum",
      accident: "Unfall",
      team: "Team",
      gallery: "Galerie",
      contact: "Kontakt",
      cta: "Termin anfragen",
    },
    hero: {
      tagline: "Carrosserie & Lackierwerk · Wolfwil",
      title_1: "Präzision in",
      title_2: "Metall & Farbe.",
      subtitle:
        "Service so individuell wie Ihr Fahrzeug und Ihre Ansprüche. Komplettabwicklung und Regulierung im Schadenfall — vom kleinen Kratzer bis zur Oldtimer-Restauration.",
      cta_primary: "Unfall melden",
      cta_secondary: "Leistungen entdecken",
      voiceover_on: "Voiceover läuft",
      voiceover_off: "Voiceover starten",
      narration:
        "Willkommen bei M P Carrosserie in Wolfwil. Wir sind Ihr Partner für individuelle Carrosseriearbeiten, Oldtimer-Restaurationen, Fahrzeugumbauten sowie Spezial- und Sonderlackierungen. Mit Leidenschaft, geschultem Auge und über zwanzig Jahren Erfahrung machen wir Ihr Fahrzeug wieder makellos.",
    },
    highlights: {
      eyebrow: "Service auf Ihrem Niveau",
      title: "Individuell wie Ihr Fahrzeug",
      items: [
        {
          tag: "01",
          title: "Komplettabwicklung im Schadenfall",
          desc: "Wir übernehmen die komplette Regulierung mit der Versicherung – Sie müssen sich um nichts kümmern.",
        },
        {
          tag: "02",
          title: "Individuelle Carrosseriearbeiten",
          desc: "Jedes Fahrzeug bekommt die Aufmerksamkeit, die es verdient. Massgeschneiderte Reparaturen, kompromisslos in Qualität.",
        },
        {
          tag: "03",
          title: "Oldtimer-Restaurationen",
          desc: "Vom Blech bis zum letzten Farbnebel – wir hauchen Klassikern neues Leben ein, originalgetreu und detailverliebt.",
        },
        {
          tag: "04",
          title: "Fahrzeugumbauten",
          desc: "Sonderlösungen, Karosserie-Anpassungen und Umbauten – sauber geplant, fachmännisch ausgeführt.",
        },
        {
          tag: "05",
          title: "Spezial- & Sonderlackierungen",
          desc: "Effektlacke, Sonderfarben, Custom-Designs. Ihre Idee – wir setzen sie um.",
        },
      ],
    },
    why: {
      eyebrow: "Warum wir",
      title: "Drei Gründe, uns zu vertrauen",
      items: [
        {
          t: "Für jedes Budget die passende Lösung",
          d: "Wir finden den richtigen Weg – vom Spot-Repair bis zur Vollrestauration. Transparent, fair und ehrlich beraten.",
        },
        {
          t: "Reparatur aller Fahrzeugmarken",
          d: "Egal ob Kleinwagen, Premium-Limousine oder E-Fahrzeug mit Hochvolttechnik – wir haben das Know-how.",
        },
        {
          t: "Wir nehmen uns Zeit und hören zu",
          d: "Sie sind kein Fall, Sie sind ein Mensch. Persönliche Beratung steht bei uns über allem.",
        },
      ],
    },
    certification: {
      eyebrow: "Qualität & Vertrauen",
      title: "Zertifiziert nach internationalen ISO-Standards",
      subtitle:
        "Unser integriertes Managementsystem ist offiziell zertifiziert durch CertifiedFirst Switzerland — geprüft und auditiert nach ISO 9001, ISO 14001 und ISO 45001.",
      standards: [
        { code: "ISO 9001", label: "Qualitätsmanagement" },
        { code: "ISO 14001", label: "Umweltmanagement" },
        { code: "ISO 45001", label: "Arbeitssicherheit" },
      ],
      scope_label: "Geltungsbereich",
      scope: "Carrosserie- & Lackierarbeiten · Unternehmensführung · Marketing · Nachhaltigkeit · Arbeitssicherheit · Infrastruktur",
      issued_label: "Erstzertifizierung",
      issued: "19.05.2026",
      valid_label: "Gültig bis",
      valid: "18.05.2029",
      issuer_label: "Ausstellende Stelle",
      issuer: "CertifiedFirst Switzerland",
      cta: "Zertifikat ansehen (PDF)",
    },
    spectrum: {
      eyebrow: "Leistungsspektrum",
      title: "Vier Spezialgebiete",
      subtitle:
        "Klicken Sie eine Karte an, um Details, Vorgehen und Beispiele zu sehen.",
      items: [
        {
          key: "hagel",
          title: "Hagelschaden",
          short: "Druck-Tech · Ausbeulen ohne Lack zu beschädigen",
          details: [
            {
              q: "Was ist Drucktech?",
              a: "Professionelles Ausbeulen, ohne die Lackstruktur zu beschädigen. Eine schonende Methode, die den originalen Werkslack erhält.",
            },
            {
              q: "Wie funktioniert es?",
              a: "Spezialwerkzeuge ermöglichen es geschultem Personal, die Beulen präzise auszudrücken – ohne den Lack anzutasten.",
            },
            {
              q: "Wofür eignet es sich?",
              a: "Ideal bei Parkschäden, klassischem Türschlag und Hagelschäden, solange der Lack nicht beschädigt wurde.",
            },
          ],
        },
        {
          key: "steinschlag",
          title: "Steinschlag",
          short: "Erste Hilfe, Reparatur oder Frontscheibenersatz",
          details: [
            {
              q: "Erste Hilfe",
              a: "Überkleben Sie einen Steinschlag sofort, damit kein Wasser und Schmutz eindringt. Lassen Sie ihn zeitnah durch einen Experten begutachten.",
            },
            {
              q: "Muss die Frontscheibe gewechselt werden?",
              a: "Bei kleinen Steinschlägen unter 5 mm kann die Scheibe ausserhalb des Sichtbereichs mit Spezialharz repariert werden. Bei grösseren Schäden oder Rissbildung wird ersetzt – wir koordinieren mit Ihrer Versicherung.",
            },
            {
              q: "Was passiert mit Vignetten oder ausländischen Spezialvignetten?",
              a: "Wir ersetzen diese und besorgen kostenlos einen Ersatz für Sie.",
            },
            {
              q: "Gratis Leihwagen im Glasschadenfall",
              a: "Bei uns bekommen Sie zu jeder Frontscheibenreparatur und jedem Ersatz gratis einen Leihwagen gestellt.",
            },
          ],
        },
        {
          key: "lack",
          title: "Lackiererei",
          short: "Vollack, Spezial- und Spot-Repair",
          details: [
            {
              q: "Neuer Lack für Ihr Liebhaberfahrzeug",
              a: "Wir ermöglichen jeden Wunsch in Bezug auf Lackierung – stöbern Sie durch unsere Galerie. Mit Leidenschaft, grösster Hingabe und einem geschulten Auge fürs Detail stehen wir Ihnen für alle Wünsche zur Verfügung.",
            },
            {
              q: "Spot Repair",
              a: "Eine Methode, um die Kosten der Lackierung möglichst gering zu halten. Die Reparatur erfolgt nur örtlich, ohne grosse Demontage der Bauteile. Vorteile: geringer Werkstattaufenthalt, kostenoptimiert, weniger Material- und Energieaufwand.",
            },
          ],
        },
        {
          key: "parkschaden",
          title: "Parkschaden",
          short: "Schneller, unkomplizierter Ablauf",
          details: [
            {
              q: "Türschlag in der Tiefgarage?",
              a: "Ein Parkschaden ist schnell passiert. Wer kennt es nicht – ein Aufenthalt in engen Tiefgaragen und plötzlich ein Türschlag auf dem Wagen.",
            },
            {
              q: "So gehen wir vor",
              a: "Kommen Sie vorbei – wir begutachten den Schaden professionell und sorgen für einen möglichst unkomplizierten Ablauf. Vom ersten Kontakt an betreuen wir Sie mit unserem ganzen Fachwissen.",
            },
          ],
        },
      ],
    },
    gallery: {
      eyebrow: "Aus der Werkstatt",
      title: "Sehen Sie unser Handwerk in Bewegung",
      subtitle:
        "Kurze Einblicke in unsere täglichen Arbeiten — jedes Video mit Voiceover, das den Prozess beschreibt.",
      play: "Video abspielen",
      stop: "Stoppen",
      videos: [
        {
          title: "Carrosseriearbeiten",
          tag: "Spenglerei",
          narration:
            "In unserer Spenglerei richten wir Karosserien millimetergenau wieder auf. Mit moderner Mess- und Richttechnik bringen wir jede Linie zurück in die originale Werksspezifikation.",
        },
        {
          title: "Lackierarbeiten",
          tag: "Lackiererei",
          narration:
            "In der staubfreien Lackierkabine setzen wir Werks-Farbcodes, Sonderfarben und Effektlacke um. Wasserlackbasierte Systeme garantieren ein perfektes, langlebiges Finish.",
        },
        {
          title: "Unfallreparaturen",
          tag: "Schadenservice",
          narration:
            "Vom Schadenrapport über die Versicherungsabwicklung bis zur vollständigen Reparatur – wir übernehmen den ganzen Prozess und übergeben Ihnen Ihr Fahrzeug wie neu.",
        },
      ],
    },
    accident: {
      eyebrow: "Unfallschaden? Was nun?",
      title: "Reibungsloser Ablauf in drei Schritten",
      bullets: [
        "Reibungsloser Ablauf und Service",
        "Abholung des Unfallwagens oder Besichtigung vor Ort nach Absprache",
        "Fachkundige Beratung",
      ],
      steps: [
        {
          n: "01",
          t: "Ruhe bewahren",
          d: "Atmen Sie tief durch. Sichern Sie die Unfallstelle und dokumentieren Sie den Schaden mit Fotos.",
        },
        {
          n: "02",
          t: "Uns kontaktieren",
          d: "Per WhatsApp, Mail, Anruf oder Live-Chat – wir holen Ihr Fahrzeug ab oder besichtigen es vor Ort.",
        },
        {
          n: "03",
          t: "Wir übernehmen",
          d: "Versicherung, Ersatzteile, Reparatur, Lackierung. Sie bekommen Ihr Fahrzeug wie neu zurück.",
        },
      ],
      cta: "Unfall jetzt melden",
    },
    team: {
      eyebrow: "Unser Team",
      title: "Zwei Geschäftsinhaber. Eine Leidenschaft.",
      members: [
        {
          name: "Dominic Müller",
          role: "Geschäftsinhaber · Leiter Spenglerei",
          bio:
            "Seit fast 20 Jahren in der Carrosserieleitung und im Kundendienst tätig. Gelernter Carrosseriespengler – nie weg vom Geschehen, immer gerne mit Hand am Werk. Spezialkurse im Bereich Hochvolttechnik (E-Fahrzeuge). Durch Tätigkeit in Kundendienst und Carrosserieleitung sowie ein Maschinenbaustudium grosses Fachwissen in Abwicklung, EDV, Buchhaltung und Materialwissen.",
          phone: "+41 79 926 40 00",
          email: "mueller@mp-carrosserie.ch",
        },
        {
          name: "Carmine Petretta",
          role: "Geschäftsinhaber · Leiter Lackiererei",
          bio:
            "Verantwortlich für die Lackiererei – Werks-Farbcodes, Sonderlackierungen und Effektlacke. Mit jahrzehntelanger Erfahrung steht er für ein makelloses Finish und ein geschultes Auge fürs Detail.",
          phone: "+41 79 926 40 09",
          email: "petretta@mp-carrosserie.ch",
        },
      ],
    },
    instagram: {
      eyebrow: "Folgen Sie uns",
      title: "@mp_carrosserie_wolfwil",
      subtitle:
        "Aktuelle Projekte, Vorher–Nachher und Werkstattmomente auf Instagram.",
      cta: "Auf Instagram öffnen",
    },
    contact: {
      eyebrow: "Wie nehme ich am besten Kontakt auf?",
      title: "Sprechen Sie mit uns",
      subtitle:
        "Kommen Sie jederzeit vorbei, nutzen Sie Live-Chat, WhatsApp, Telefon oder Mail. Falls Sie in der Hektik des Alltags gefangen sind, schicken Sie uns qualitativ hochwertige Fotos – wir erstellen Ihnen ein professionelles Angebot.",
      form: {
        name: "Name",
        email: "E-Mail",
        phone: "Telefon",
        subject: "Betreff",
        message: "Ihre Nachricht",
        submit: "Anfrage senden",
        sending: "Wird gesendet…",
        success: "Vielen Dank — wir melden uns!",
        error: "Etwas ging schief. Bitte versuchen Sie es erneut.",
      },
      info: {
        address_label: "Adresse",
        address: "Milchgasse 4, 4628 Wolfwil",
        phone_label: "Telefon",
        email_label: "E-Mail",
        hours_label: "Öffnungszeiten",
        hours: "Mo–Fr: 08:00 – 18:00 · Sa: nach Vereinbarung",
      },
    },
    chatbot: {
      title: "MP Assistant",
      subtitle: "Live-Chat · Claude Sonnet 4.5",
      placeholder: "Schreiben Sie Ihre Frage…",
      greeting:
        "Grüezi! Ich bin der Live-Chat von MP Carrosserie. Wie kann ich Ihnen helfen — Unfall, Lackierung, Steinschlag oder Termin?",
      open: "Chat öffnen",
      send: "Senden",
    },
    footer: {
      rights: "Alle Rechte vorbehalten",
      impressum: "Impressum",
      tagline: "Carrosserie & Lackierwerk · Wolfwil",
    },
  },

  fr: {
    nav: {
      services: "Prestations",
      spectrum: "Spectre",
      accident: "Accident",
      team: "Équipe",
      gallery: "Galerie",
      contact: "Contact",
      cta: "Demander un RDV",
    },
    hero: {
      tagline: "Carrosserie & Peinture · Wolfwil",
      title_1: "Précision en",
      title_2: "métal & couleur.",
      subtitle:
        "Un service aussi individuel que votre véhicule. Gestion complète et règlement des sinistres — de la petite rayure à la restauration de voiture ancienne.",
      cta_primary: "Déclarer un accident",
      cta_secondary: "Voir les prestations",
      voiceover_on: "Voix off active",
      voiceover_off: "Démarrer la voix off",
      narration:
        "Bienvenue chez M P Carrosserie à Wolfwil. Nous sommes votre partenaire pour les travaux de carrosserie sur mesure, la restauration de voitures anciennes, les transformations et les peintures spéciales.",
    },
    highlights: {
      eyebrow: "Service à votre niveau",
      title: "Individuel comme votre véhicule",
      items: [
        { tag: "01", title: "Gestion complète du sinistre", desc: "Nous prenons en charge le règlement complet avec l'assurance — vous n'avez à vous occuper de rien." },
        { tag: "02", title: "Travaux de carrosserie individuels", desc: "Chaque véhicule reçoit l'attention qu'il mérite. Réparations sur mesure, qualité sans compromis." },
        { tag: "03", title: "Restauration de voitures anciennes", desc: "De la tôle au dernier voile de peinture, nous redonnons vie aux classiques, fidèlement et avec passion." },
        { tag: "04", title: "Transformations de véhicules", desc: "Solutions spéciales, modifications de carrosserie et transformations — proprement planifiées, expertement exécutées." },
        { tag: "05", title: "Peintures spéciales", desc: "Vernis à effet, couleurs spéciales, designs custom. Votre idée — nous la réalisons." },
      ],
    },
    why: {
      eyebrow: "Pourquoi nous",
      title: "Trois raisons de nous faire confiance",
      items: [
        { t: "Une solution pour chaque budget", d: "Du Spot-Repair à la restauration complète. Conseil transparent, juste et honnête." },
        { t: "Réparation toutes marques", d: "Petites voitures, berlines premium ou véhicules électriques haute tension — nous avons le savoir-faire." },
        { t: "Nous prenons le temps de vous écouter", d: "Vous n'êtes pas un dossier, vous êtes une personne. Le conseil personnalisé prime sur tout." },
      ],
    },
    certification: {
      eyebrow: "Qualité & Confiance",
      title: "Certifié selon les normes ISO internationales",
      subtitle:
        "Notre système de management intégré est officiellement certifié par CertifiedFirst Switzerland — audité selon les normes ISO 9001, ISO 14001 et ISO 45001.",
      standards: [
        { code: "ISO 9001", label: "Management de la qualité" },
        { code: "ISO 14001", label: "Management environnemental" },
        { code: "ISO 45001", label: "Santé & sécurité au travail" },
      ],
      scope_label: "Champ d'application",
      scope: "Carrosserie & peinture · Direction d'entreprise · Marketing · Durabilité · Sécurité au travail · Infrastructure",
      issued_label: "Première certification",
      issued: "19.05.2026",
      valid_label: "Valable jusqu'au",
      valid: "18.05.2029",
      issuer_label: "Organisme certificateur",
      issuer: "CertifiedFirst Switzerland",
      cta: "Voir le certificat (PDF)",
    },
    spectrum: {
      eyebrow: "Spectre de prestations",
      title: "Quatre spécialités",
      subtitle: "Cliquez sur une carte pour voir les détails et la démarche.",
      items: [
        {
          key: "hagel",
          title: "Grêle",
          short: "Drucktech · débosselage sans toucher la peinture",
          details: [
            { q: "Qu'est-ce que le Drucktech ?", a: "Débosselage professionnel sans abîmer la peinture d'origine. Une méthode douce qui conserve la peinture d'usine." },
            { q: "Comment ça marche ?", a: "Des outils spéciaux permettent à notre personnel formé de repousser précisément les bosses, sans toucher au vernis." },
            { q: "Pour quoi est-ce adapté ?", a: "Idéal pour les bris de parking, coups de portière classiques et grêle — tant que la peinture est intacte." },
          ],
        },
        {
          key: "steinschlag",
          title: "Impact de gravillon",
          short: "Premiers gestes, réparation ou pare-brise neuf",
          details: [
            { q: "Premiers gestes", a: "Recouvrez immédiatement l'impact pour éviter eau et saleté, puis faites-le examiner rapidement par un expert." },
            { q: "Faut-il remplacer le pare-brise ?", a: "Pour les impacts < 5 mm hors champ de vision, une réparation à la résine spéciale suffit. Sinon, remplacement — nous coordonnons avec votre assurance." },
            { q: "Et les vignettes (suisses ou étrangères) ?", a: "Nous les remplaçons gratuitement." },
            { q: "Véhicule de prêt gratuit", a: "Pour chaque réparation ou remplacement de pare-brise, nous fournissons un véhicule de prêt gratuit." },
          ],
        },
        {
          key: "lack",
          title: "Peinture",
          short: "Peinture complète, spéciale et Spot-Repair",
          details: [
            { q: "Une nouvelle peinture pour votre véhicule de cœur", a: "Tout est possible — parcourez notre galerie. Passion, dévouement et œil aiguisé pour le détail à votre service." },
            { q: "Spot Repair", a: "Méthode pour limiter les coûts : réparation locale, sans démontage important. Avantages : moins de temps en atelier, coûts optimisés, moins de matériaux et d'énergie." },
          ],
        },
        {
          key: "parkschaden",
          title: "Dégât de parking",
          short: "Processus rapide et simple",
          details: [
            { q: "Coup de portière en parking ?", a: "Cela arrive à tout le monde — un séjour dans un parking étroit, et soudain une trace sur votre voiture." },
            { q: "Notre démarche", a: "Passez nous voir — nous évaluons professionnellement le dégât et assurons un traitement sans complications, du premier contact jusqu'à la livraison." },
          ],
        },
      ],
    },
    gallery: {
      eyebrow: "Depuis l'atelier",
      title: "Notre savoir-faire en mouvement",
      subtitle: "Brefs aperçus de notre travail quotidien — chaque vidéo avec voix off décrivant le processus.",
      play: "Lire la vidéo",
      stop: "Stop",
      videos: [
        { title: "Travaux de carrosserie", tag: "Tôlerie", narration: "Dans notre atelier de tôlerie, nous redressons les carrosseries au millimètre près. Avec des techniques modernes de mesure et de redressage, nous restaurons chaque ligne aux spécifications d'origine." },
        { title: "Peinture", tag: "Cabine", narration: "En cabine sans poussière, nous appliquons codes constructeur, couleurs spéciales et vernis à effet. Les systèmes à base d'eau garantissent une finition parfaite et durable." },
        { title: "Réparations après accident", tag: "Sinistre", narration: "Du constat au règlement d'assurance jusqu'à la réparation complète — nous gérons tout le processus et vous remettons votre véhicule comme neuf." },
      ],
    },
    accident: {
      eyebrow: "Accident ? Et maintenant ?",
      title: "Un déroulement fluide en trois étapes",
      bullets: ["Déroulement et service fluides", "Enlèvement du véhicule ou visite sur place selon entente", "Conseils d'experts"],
      steps: [
        { n: "01", t: "Gardez votre calme", d: "Respirez. Sécurisez les lieux et documentez le sinistre en photos." },
        { n: "02", t: "Contactez-nous", d: "Via WhatsApp, e-mail, appel ou live-chat. Nous enlevons le véhicule ou venons sur place." },
        { n: "03", t: "Nous gérons", d: "Assurance, pièces, réparation, peinture. Vous récupérez votre véhicule comme neuf." },
      ],
      cta: "Déclarer maintenant",
    },
    team: {
      eyebrow: "Notre équipe",
      title: "Deux propriétaires. Une passion.",
      members: [
        { name: "Dominic Müller", role: "Propriétaire · Chef tôlerie", bio: "Près de 20 ans dans la direction de carrosserie et le service client. Tôlier de carrosserie de formation — toujours sur le terrain. Cours spéciaux en haute tension (véhicules électriques). Études de génie mécanique : grandes compétences en comptabilité et connaissance des matériaux.", phone: "+41 79 926 40 00", email: "mueller@mp-carrosserie.ch" },
        { name: "Carmine Petretta", role: "Propriétaire · Chef peinture", bio: "Responsable de la peinture — codes constructeur, peintures spéciales et vernis à effet. Avec des décennies d'expérience, il garantit une finition impeccable et un œil aiguisé pour le détail.", phone: "+41 79 926 40 09", email: "petretta@mp-carrosserie.ch" },
      ],
    },
    instagram: { eyebrow: "Suivez-nous", title: "@mp_carrosserie_wolfwil", subtitle: "Projets actuels, avant/après et coulisses sur Instagram.", cta: "Ouvrir Instagram" },
    contact: {
      eyebrow: "Comment nous contacter ?",
      title: "Parlons ensemble",
      subtitle: "Passez quand vous voulez, utilisez le live-chat, WhatsApp, le téléphone ou l'e-mail. Pris dans le tourbillon du quotidien ? Envoyez-nous de bonnes photos — nous établissons une offre professionnelle.",
      form: { name: "Nom", email: "E-mail", phone: "Téléphone", subject: "Sujet", message: "Votre message", submit: "Envoyer", sending: "Envoi…", success: "Merci — nous revenons vers vous !", error: "Quelque chose a échoué." },
      info: { address_label: "Adresse", address: "Milchgasse 4, 4628 Wolfwil", phone_label: "Téléphone", email_label: "E-mail", hours_label: "Horaires", hours: "Lun–Ven : 08h00 – 18h00 · Sam : sur RDV" },
    },
    chatbot: { title: "MP Assistant", subtitle: "Live-Chat · Claude Sonnet 4.5", placeholder: "Posez votre question…", greeting: "Bonjour ! Je suis le live-chat de MP Carrosserie. Comment puis-je vous aider ?", open: "Ouvrir le chat", send: "Envoyer" },
    footer: { rights: "Tous droits réservés", impressum: "Mentions légales", tagline: "Carrosserie & Peinture · Wolfwil" },
  },

  it: {
    nav: { services: "Servizi", spectrum: "Spettro", accident: "Incidente", team: "Team", gallery: "Galleria", contact: "Contatto", cta: "Prenota" },
    hero: {
      tagline: "Carrozzeria & Verniciatura · Wolfwil",
      title_1: "Precisione in",
      title_2: "metallo e colore.",
      subtitle: "Un servizio personalizzato come la vostra auto. Gestione completa dei sinistri — dal piccolo graffio al restauro di un'auto d'epoca.",
      cta_primary: "Segnala incidente",
      cta_secondary: "Scopri i servizi",
      voiceover_on: "Voce attiva",
      voiceover_off: "Avvia voce",
      narration: "Benvenuti in M P Carrosserie a Wolfwil. Siamo il vostro partner per lavori di carrozzeria su misura, restauri di auto d'epoca, trasformazioni e verniciature speciali.",
    },
    highlights: {
      eyebrow: "Servizio al vostro livello",
      title: "Personalizzato come la vostra auto",
      items: [
        { tag: "01", title: "Gestione completa del sinistro", desc: "Ci occupiamo dell'intero processo con l'assicurazione — voi non dovete pensare a nulla." },
        { tag: "02", title: "Lavori di carrozzeria personalizzati", desc: "Ogni veicolo riceve l'attenzione che merita. Riparazioni su misura, qualità senza compromessi." },
        { tag: "03", title: "Restauri di auto d'epoca", desc: "Dalla lamiera all'ultima velatura — diamo nuova vita ai classici, fedeli all'originale." },
        { tag: "04", title: "Trasformazioni di veicoli", desc: "Soluzioni speciali e modifiche di carrozzeria, pianificate bene ed eseguite con maestria." },
        { tag: "05", title: "Verniciature speciali", desc: "Vernici a effetto, colori speciali, design custom. La vostra idea — la realizziamo noi." },
      ],
    },
    why: {
      eyebrow: "Perché noi",
      title: "Tre motivi per fidarsi di noi",
      items: [
        { t: "Una soluzione per ogni budget", d: "Dallo Spot-Repair al restauro completo. Consulenza trasparente, equa, onesta." },
        { t: "Riparazioni di tutte le marche", d: "Utilitarie, berline premium o veicoli elettrici ad alta tensione — abbiamo il know-how." },
        { t: "Ascoltiamo davvero", d: "Non siete una pratica, siete una persona. La consulenza personale viene prima di tutto." },
      ],
    },
    certification: {
      eyebrow: "Qualità & Fiducia",
      title: "Certificati secondo gli standard ISO internazionali",
      subtitle:
        "Il nostro sistema di gestione integrato è ufficialmente certificato da CertifiedFirst Switzerland — verificato secondo le norme ISO 9001, ISO 14001 e ISO 45001.",
      standards: [
        { code: "ISO 9001", label: "Gestione della qualità" },
        { code: "ISO 14001", label: "Gestione ambientale" },
        { code: "ISO 45001", label: "Salute & sicurezza sul lavoro" },
      ],
      scope_label: "Campo di applicazione",
      scope: "Carrozzeria & verniciatura · Direzione aziendale · Marketing · Sostenibilità · Sicurezza sul lavoro · Infrastruttura",
      issued_label: "Prima certificazione",
      issued: "19.05.2026",
      valid_label: "Valido fino al",
      valid: "18.05.2029",
      issuer_label: "Ente certificatore",
      issuer: "CertifiedFirst Switzerland",
      cta: "Vedi certificato (PDF)",
    },
    spectrum: {
      eyebrow: "Spettro di servizi",
      title: "Quattro specialità",
      subtitle: "Cliccate su una scheda per i dettagli.",
      items: [
        { key: "hagel", title: "Grandine", short: "Drucktech · disammaccatura senza vernice", details: [
          { q: "Cos'è il Drucktech?", a: "Disammaccatura professionale senza danneggiare la vernice originale." },
          { q: "Come funziona?", a: "Strumenti speciali permettono al personale qualificato di spingere fuori le ammaccature con precisione, senza toccare la vernice." },
          { q: "A cosa si presta?", a: "Ideale per danni da parcheggio, colpi di portiera e grandine, se la vernice è intatta." },
        ]},
        { key: "steinschlag", title: "Scheggiatura", short: "Pronto soccorso, riparazione o sostituzione", details: [
          { q: "Primi gesti", a: "Coprite subito la scheggiatura con un cerotto adesivo per evitare acqua e sporco. Fatela esaminare da un esperto." },
          { q: "Serve sostituire il parabrezza?", a: "Per scheggiature < 5 mm fuori dal campo visivo è possibile riparare con resina speciale. Altrimenti si sostituisce — ci coordiniamo con la vostra assicurazione." },
          { q: "Vignette / contrassegni esteri?", a: "Li sostituiamo e li procuriamo gratuitamente." },
          { q: "Auto sostitutiva gratuita", a: "Per ogni riparazione o sostituzione del parabrezza vi forniamo un'auto sostitutiva gratuita." },
        ]},
        { key: "lack", title: "Verniciatura", short: "Vernice completa, speciale e Spot-Repair", details: [
          { q: "Una nuova vernice per la vostra auto del cuore", a: "Tutto è possibile — sfogliate la galleria. Passione, dedizione e occhio per il dettaglio." },
          { q: "Spot Repair", a: "Metodo per contenere i costi: riparazione locale senza smontaggi. Vantaggi: meno tempo in officina, costi ottimizzati, minor consumo di materiale ed energia." },
        ]},
        { key: "parkschaden", title: "Danno da parcheggio", short: "Procedura rapida e semplice", details: [
          { q: "Colpo di portiera in garage?", a: "Capita a tutti — autorimesse strette e improvvisamente un segno sull'auto." },
          { q: "Come procediamo", a: "Passate da noi — valutiamo il danno professionalmente e garantiamo un iter senza complicazioni." },
        ]},
      ],
    },
    gallery: {
      eyebrow: "Dall'officina",
      title: "Il nostro lavoro in movimento",
      subtitle: "Brevi scorci del quotidiano — ogni video con voce fuori campo che descrive il processo.",
      play: "Riproduci video",
      stop: "Stop",
      videos: [
        { title: "Lavori di carrozzeria", tag: "Lattoneria", narration: "In lattoneria raddrizziamo le carrozzerie al millimetro. Tecniche di misura moderne riportano ogni linea alle specifiche originali." },
        { title: "Verniciatura", tag: "Cabina", narration: "In cabina sterile applichiamo codici originali, colori speciali e vernici a effetto. Sistemi all'acqua per finiture perfette e durature." },
        { title: "Riparazioni post-incidente", tag: "Sinistro", narration: "Dalla constatazione alla pratica assicurativa fino alla riparazione completa — gestiamo tutto e vi restituiamo l'auto come nuova." },
      ],
    },
    accident: {
      eyebrow: "Incidente? E adesso?",
      title: "Un iter fluido in tre passi",
      bullets: ["Iter e servizio fluidi", "Ritiro o sopralluogo su appuntamento", "Consulenza esperta"],
      steps: [
        { n: "01", t: "Mantenete la calma", d: "Respirate. Mettete in sicurezza il luogo e documentate il danno con foto." },
        { n: "02", t: "Contattateci", d: "Via WhatsApp, mail, telefono o live-chat. Ritiriamo il veicolo o veniamo sul posto." },
        { n: "03", t: "Ci pensiamo noi", d: "Assicurazione, ricambi, riparazione, verniciatura. L'auto torna come nuova." },
      ],
      cta: "Segnala ora",
    },
    team: {
      eyebrow: "Il nostro team",
      title: "Due titolari. Una passione.",
      members: [
        { name: "Dominic Müller", role: "Titolare · Responsabile lattoneria", bio: "Quasi 20 anni nella direzione di carrozzeria e nel servizio clienti. Lattoniere di carrozzeria di formazione — sempre sul campo. Corsi speciali in alta tensione (auto elettriche). Studi di ingegneria meccanica: solide competenze in contabilità e materiali.", phone: "+41 79 926 40 00", email: "mueller@mp-carrosserie.ch" },
        { name: "Carmine Petretta", role: "Titolare · Responsabile verniciatura", bio: "Responsabile della verniciatura — codici originali, verniciature speciali ed effetti. Decenni di esperienza per finiture impeccabili.", phone: "+41 79 926 40 09", email: "petretta@mp-carrosserie.ch" },
      ],
    },
    instagram: { eyebrow: "Seguiteci", title: "@mp_carrosserie_wolfwil", subtitle: "Progetti, prima/dopo e dietro le quinte su Instagram.", cta: "Apri Instagram" },
    contact: {
      eyebrow: "Come contattarci?",
      title: "Parliamone",
      subtitle: "Passate quando volete, usate il live-chat, WhatsApp, telefono o mail. Inviateci foto di buona qualità — vi prepariamo un'offerta professionale.",
      form: { name: "Nome", email: "E-mail", phone: "Telefono", subject: "Oggetto", message: "Il vostro messaggio", submit: "Invia", sending: "Invio…", success: "Grazie — vi ricontatteremo!", error: "Qualcosa è andato storto." },
      info: { address_label: "Indirizzo", address: "Milchgasse 4, 4628 Wolfwil", phone_label: "Telefono", email_label: "E-mail", hours_label: "Orari", hours: "Lun–Ven: 08:00 – 18:00 · Sab: su appuntamento" },
    },
    chatbot: { title: "MP Assistant", subtitle: "Live-Chat · Claude Sonnet 4.5", placeholder: "Scrivete la vostra domanda…", greeting: "Salve! Sono il live-chat di MP Carrosserie. Come posso aiutarla?", open: "Apri chat", send: "Invia" },
    footer: { rights: "Tutti i diritti riservati", impressum: "Note legali", tagline: "Carrozzeria & Verniciatura · Wolfwil" },
  },

  en: {
    nav: { services: "Services", spectrum: "Spectrum", accident: "Accident", team: "Team", gallery: "Gallery", contact: "Contact", cta: "Book a slot" },
    hero: {
      tagline: "Bodyshop & Paint · Wolfwil",
      title_1: "Precision in",
      title_2: "metal & color.",
      subtitle: "Service as individual as your car. Full insurance handling — from the smallest scratch to full classic-car restoration.",
      cta_primary: "Report an accident",
      cta_secondary: "Explore services",
      voiceover_on: "Voiceover playing",
      voiceover_off: "Start voiceover",
      narration: "Welcome to M P Carrosserie in Wolfwil. We're your partner for custom bodywork, classic-car restoration, vehicle conversions and special paint jobs. Over two decades of Swiss craftsmanship.",
    },
    highlights: {
      eyebrow: "Service on your level",
      title: "As individual as your car",
      items: [
        { tag: "01", title: "Full insurance handling", desc: "We handle the entire claim with your insurer — you take care of nothing." },
        { tag: "02", title: "Custom bodywork", desc: "Every car gets the attention it deserves. Tailored repairs, no compromise on quality." },
        { tag: "03", title: "Classic-car restoration", desc: "From bare metal to the final clear coat — we bring classics back to life, faithfully." },
        { tag: "04", title: "Vehicle conversions", desc: "Bespoke solutions and body modifications — properly planned, expertly executed." },
        { tag: "05", title: "Special paint jobs", desc: "Effect lacquers, custom colors, one-off designs. Your idea — we make it real." },
      ],
    },
    why: {
      eyebrow: "Why us",
      title: "Three reasons to trust us",
      items: [
        { t: "A solution for every budget", d: "From spot repair to full restoration. Transparent, fair, honest advice." },
        { t: "All makes & models", d: "City cars, premium sedans, high-voltage EVs — we have the know-how." },
        { t: "We take time and listen", d: "You're not a case file, you're a person. Personal advice always comes first." },
      ],
    },
    certification: {
      eyebrow: "Quality & Trust",
      title: "Certified to international ISO standards",
      subtitle:
        "Our integrated management system is officially certified by CertifiedFirst Switzerland — audited to ISO 9001, ISO 14001 and ISO 45001.",
      standards: [
        { code: "ISO 9001", label: "Quality Management" },
        { code: "ISO 14001", label: "Environmental Management" },
        { code: "ISO 45001", label: "Occupational Health & Safety" },
      ],
      scope_label: "Scope",
      scope: "Bodywork & paint · Business management · Marketing · Sustainability · Occupational safety · Infrastructure",
      issued_label: "Initial certification",
      issued: "19.05.2026",
      valid_label: "Valid until",
      valid: "18.05.2029",
      issuer_label: "Issuing body",
      issuer: "CertifiedFirst Switzerland",
      cta: "View certificate (PDF)",
    },
    spectrum: {
      eyebrow: "Service spectrum",
      title: "Four specialties",
      subtitle: "Click a card for details and process.",
      items: [
        { key: "hagel", title: "Hail damage", short: "Drucktech · dent removal without paint damage", details: [
          { q: "What is Drucktech?", a: "Professional dent removal without damaging the paint structure — preserves your factory finish." },
          { q: "How does it work?", a: "Specialist tools allow our trained technicians to push dents out precisely, without touching the paint." },
          { q: "What is it for?", a: "Ideal for parking dents, classic door-strike marks and hail damage — as long as the paint isn't broken." },
        ]},
        { key: "steinschlag", title: "Stone chip", short: "First aid, repair or full windscreen replacement", details: [
          { q: "First aid", a: "Cover a stone chip immediately so no water or dirt gets in, then have it inspected by an expert promptly." },
          { q: "Does the windscreen need to be replaced?", a: "Chips < 5mm outside the field of vision can be resin-repaired. Larger damage or cracks: replacement — we coordinate with your insurer." },
          { q: "What about vignettes / foreign permits?", a: "We replace them and source replacements free of charge." },
          { q: "Free courtesy car for glass claims", a: "Every windscreen repair or replacement comes with a free courtesy car." },
        ]},
        { key: "lack", title: "Paintwork", short: "Full paint, custom and Spot-Repair", details: [
          { q: "A new coat for your dream car", a: "Anything is possible — browse our gallery. Passion, dedication and an eye for detail." },
          { q: "Spot Repair", a: "A cost-saving local repair without major disassembly. Advantages: less workshop time, lower cost, less material and energy." },
        ]},
        { key: "parkschaden", title: "Parking damage", short: "Quick, uncomplicated process", details: [
          { q: "Door ding in the garage?", a: "It happens to everyone — a tight underground parking spot and suddenly there's a mark." },
          { q: "How we proceed", a: "Drop by — we assess the damage professionally and keep the process simple from first contact to handover." },
        ]},
      ],
    },
    gallery: {
      eyebrow: "From the shop floor",
      title: "Our craft in motion",
      subtitle: "Short glimpses into our daily work — each video with voiceover describing the process.",
      play: "Play video",
      stop: "Stop",
      videos: [
        { title: "Bodywork", tag: "Panel shop", narration: "In our panel shop we straighten bodies to the millimeter. Modern measurement and alignment tech bring every line back to factory spec." },
        { title: "Paintwork", tag: "Booth", narration: "Inside the dust-free paint booth we apply OEM color codes, custom colors and effect lacquers. Waterborne paint systems for a flawless, lasting finish." },
        { title: "Accident repair", tag: "Claims", narration: "From damage report and insurance handling all the way to full repair — we manage the entire process and hand you your car back like new." },
      ],
    },
    accident: {
      eyebrow: "Accident? What now?",
      title: "Smooth process in three steps",
      bullets: ["Smooth process and service", "Pickup or on-site inspection by arrangement", "Expert advice"],
      steps: [
        { n: "01", t: "Stay calm", d: "Breathe. Secure the scene and document the damage with photos." },
        { n: "02", t: "Contact us", d: "Via WhatsApp, email, call or live-chat. We pick up your vehicle or come on-site." },
        { n: "03", t: "We handle it", d: "Insurance, parts, repair, paint. You get your car back like new." },
      ],
      cta: "Report now",
    },
    team: {
      eyebrow: "Our team",
      title: "Two owners. One obsession.",
      members: [
        { name: "Dominic Müller", role: "Owner · Head of Panel Shop", bio: "Nearly 20 years in bodyshop management and customer service. Trained body-panel technician — always hands-on. Specialist courses in high-voltage EV technology. Mechanical engineering studies: deep knowledge in administration and materials.", phone: "+41 79 926 40 00", email: "mueller@mp-carrosserie.ch" },
        { name: "Carmine Petretta", role: "Owner · Head of Paint", bio: "In charge of paintwork — OEM color codes, custom finishes and effect lacquers. Decades of experience for an impeccable finish.", phone: "+41 79 926 40 09", email: "petretta@mp-carrosserie.ch" },
      ],
    },
    instagram: { eyebrow: "Follow us", title: "@mp_carrosserie_wolfwil", subtitle: "Current projects, before/after and behind-the-scenes on Instagram.", cta: "Open Instagram" },
    contact: {
      eyebrow: "How to reach us?",
      title: "Let's talk",
      subtitle: "Drop by anytime, use live-chat, WhatsApp, phone or email. Stuck in daily life? Send us high-quality photos — we'll prepare a professional quote.",
      form: { name: "Name", email: "Email", phone: "Phone", subject: "Subject", message: "Your message", submit: "Send request", sending: "Sending…", success: "Thank you — we'll be in touch!", error: "Something went wrong." },
      info: { address_label: "Address", address: "Milchgasse 4, 4628 Wolfwil", phone_label: "Phone", email_label: "Email", hours_label: "Hours", hours: "Mon–Fri: 08:00 – 18:00 · Sat: by appointment" },
    },
    chatbot: { title: "MP Assistant", subtitle: "Live-Chat · Claude Sonnet 4.5", placeholder: "Ask anything…", greeting: "Hi! I'm the live-chat of MP Carrosserie. How can I help — accident, paint, stone chip or a quote?", open: "Open chat", send: "Send" },
    footer: { rights: "All rights reserved", impressum: "Legal Notice", tagline: "Bodyshop & Paint · Wolfwil" },
  },
};

const LanguageContext = createContext({
  lang: "de",
  setLang: () => {},
  t: translations.de,
});

export function LanguageProvider({ children }) {
  const initial =
    typeof window !== "undefined"
      ? localStorage.getItem("mp_lang") || "de"
      : "de";
  const [lang, setLangState] = useState(initial);

  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("mp_lang", newLang);
    }
  }, []);

  const value = {
    lang,
    setLang,
    t: translations[lang] || translations.de,
  };

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useI18n() {
  return useContext(LanguageContext);
}

export const SUPPORTED_LANGS = [
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
];
